import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ButtonGroup } from "../../core/components/buttons/ButtonGroup";
import { ProgressBar } from "../../core/components/progress/ProgressBar";
import { Body } from "../../core/styles/stringStyles";
import { hexWithOpacity } from "../../core/utils/hexWithOpacity";
import { FOOTER_HEIGHT } from "../navigation/components/Footer";
import { HEADER_HEIGHT } from "../navigation/components/Header";
import { useHeaderBackContext } from "../navigation/context/useHeaderContext";

type Props = {
  children: React.ReactNode;
  hideBackButton?: boolean;
  handleBack?: () => void;
  nextButton?: {
    text?: string;
    onClick: () => void;
    disabled?: boolean;
    currentStep?: number;
    numberOfSteps?: number;
    loading?: boolean;
  };
  textButton?: {
    disabled?: boolean;
    text: string;
    onClick: () => void;
    loading?: boolean;
  };
  progress?: {
    currentStep: number;
    numberOfSteps?: number;
    progressBarFillColor?: string;
  };
};
export const OnboardingScreenContainer = ({
  children,
  nextButton,
  textButton,
  progress,
  handleBack,
  hideBackButton,
}: Props) => {
  const { t } = useTranslation();
  useHeaderBackContext({ handleBack, hideBackButton });

  return (
    <>
      <ContentContainer>
        <ChildrenContainer>{children}</ChildrenContainer>
      </ContentContainer>
      <FooterContainer>
        {progress && (
          <ProgressContainer>
            <ProgressBar
              completedSteps={progress?.currentStep}
              totalSteps={progress?.numberOfSteps ?? 6}
              fillColor={progress?.progressBarFillColor}
            />
            <Body>
              {t("PROGRESS_STEP", {
                currentStep: progress.currentStep,
                totalSteps: progress.numberOfSteps ?? 6,
              })}
            </Body>
          </ProgressContainer>
        )}
        <StyledButtonGroup
          mainButton={
            nextButton
              ? {
                  ...nextButton,
                  text: nextButton.text ?? t("NEXT"),
                  iconName: "ArrowRight",
                }
              : undefined
          }
          textButton={textButton}
        />
      </FooterContainer>
    </>
  );
};

const ContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: 640px;
  width: 100%;
  min-height: calc(100vh - ${HEADER_HEIGHT}px);
  min-height: calc(100dvh - ${HEADER_HEIGHT}px);
  align-items: center;
  justify-content: flex-start;
  background-color: ${({ theme }) => theme.colors.background};
  gap: 20px;
  overflow: hidden;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  flex-direction: column-reverse;
  justify-content: center;
  align-items: center;
  width: 100%;
  > .main-button {
    width: 100%;
    max-width: 370px;
  }
  > .text-button {
  }
`;

const FooterContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-top: 1px solid ${({ theme }) => theme.colors.light};
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  bottom: 0;
  left: 0;
  padding-bottom: ${FOOTER_HEIGHT}px;
`;

const ChildrenContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  gap: 40px;
  padding: 20px 20px 300px 20px;
  overflow-y: auto;
`;

const ProgressContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 5px 10px 15px 10px;
  max-width: 640px;

  > ${Body} {
    color: ${({ theme }) => hexWithOpacity(theme.colors.dark, 0.3)};
    width: max-content;
    flex-shrink: 0;
  }

  > .progress-bar {
    width: inherit;
  }
`;
